// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";
import { Button, Form } from "react-bootstrap";
import QRCode from "react-qr-code";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction } from "actions";
import { DataState, UserStateAWSError } from "types";
import { isSet } from "shared/utils/utils";

import LoginErrorStatus from "./LoginErrorStatus";

interface IUserMfaSettingsProps {
  isRfidUser: boolean;
  mfaEnabled: boolean | undefined;
  mfaSetupUri: string | undefined;
  mfaSecret: string | undefined;
  dataState: DataState;
  awsErrorFeedback: UserStateAWSError | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IUserMfaSettingsProps = {
    isRfidUser: !!state.site.user?.isRfidUser,
    mfaEnabled: state.gui.login.mfaEnabled,
    mfaSetupUri: state.gui.login.mfaSetupUri,
    mfaSecret: state.gui.login.mfaSecret,
    dataState: state.scheduler.mfaDataState,
    awsErrorFeedback: state.gui.login.userStateAWSFeedback,
  };
  return props;
};

interface IUserMfaSettingsState {
  code: string;
}

interface IUserMfaSettingsDispatch {
  onLoadStatus: () => void;
  onStartSetup: () => void;
  onCancelSetup: () => void;
  onVerifyCode: (code: string) => void;
  onDisable: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserMfaSettingsDispatch = {
    onLoadStatus: () => {
      dispatch(raiseAction(ActionType.ACT_MFA_STATUS_REQ));
    },
    onStartSetup: () => {
      dispatch(raiseAction(ActionType.ACT_MFA_SETUP_REQ));
    },
    onCancelSetup: () => {
      dispatch(raiseAction(ActionType.ACT_MFA_SETUP_CANCEL));
    },
    onVerifyCode: (code: string) => {
      dispatch(raiseAction(ActionType.ACT_MFA_VERIFY_REQ, code));
    },
    onDisable: () => {
      dispatch(raiseAction(ActionType.ACT_MFA_DISABLE_REQ));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class UserMfaSettingsComponent extends React.Component<
  IUserMfaSettingsProps & IUserMfaSettingsDispatch,
  IUserMfaSettingsState
> {
  state: IUserMfaSettingsState = {
    code: "",
  };

  public componentDidMount() {
    if (!this.props.isRfidUser) {
      this.props.onLoadStatus();
    }
  }

  public render() {
    // MFA is only available on the standard user pool
    if (this.props.isRfidUser) {
      return null;
    }
    return (
      <div className="mt-4">
        <h1>
          <FormattedMessage id="Two-Factor Authentication" />
        </h1>
        {this.props.mfaEnabled === undefined ?
          <FormattedMessage id="Loading..." />
        : this.props.mfaEnabled ?
          this.renderEnabled()
        : isSet(this.props.mfaSetupUri) ?
          this.renderSetup()
        : this.renderDisabled()}
        {this.props.dataState === DataState.SAVE_FAIL &&
          (this.props.awsErrorFeedback === UserStateAWSError.CODE_MISMATCH ?
            <div className="alert alert-danger mt-2">
              <FormattedMessage id="The entered code is not valid - please try again." />
            </div>
          : <LoginErrorStatus />)}
      </div>
    );
  }

  private renderEnabled() {
    return (
      <>
        <p>
          <FormattedMessage id="Two-factor authentication is enabled for your account." />
        </p>
        <Button
          className="infoScreenButton"
          variant="danger"
          id="disableMfa"
          disabled={this.props.dataState === DataState.SAVE_REQ}
          onClick={(_event) => this.props.onDisable()}
        >
          <FormattedMessage id="Disable Two-Factor Authentication" />
        </Button>
      </>
    );
  }

  private renderDisabled() {
    return (
      <>
        <p>
          <FormattedMessage id="You can additionally protect your account with time-based one-time passwords from an authenticator app." />
        </p>
        <Button
          className="infoScreenButton"
          variant="outline-success"
          id="enableMfa"
          disabled={this.props.dataState === DataState.SAVE_REQ}
          onClick={(_event) => this.props.onStartSetup()}
        >
          <FormattedMessage id="Enable Two-Factor Authentication" />
        </Button>
      </>
    );
  }

  private renderSetup() {
    return (
      <>
        <p>
          <FormattedMessage id="Scan the QR code with your authenticator app and enter the generated code below." />
        </p>
        <div style={{ background: "white", padding: "12px", width: "fit-content" }}>
          <QRCode value={this.props.mfaSetupUri!} size={180} />
        </div>
        <p className="mt-2">
          <FormattedMessage id="Alternatively, enter this secret manually:" />{" "}
          <code>{this.props.mfaSecret}</code>
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <table>
            <tbody>
              <tr>
                <td>
                  <FormattedMessage id="Code" />:
                </td>
                <td>
                  <Form.Control
                    type="text"
                    name="mfaSetupCode"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    value={this.state.code}
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                    onChange={(event) => {
                      this.setState({ code: event?.currentTarget.value });
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="buttonbox">
            <Button
              type="submit"
              className="infoScreenButton"
              variant="primary"
              id="verifyMfa"
              disabled={this.props.dataState === DataState.SAVE_REQ || !this.isCodeValid()}
              onClick={(_event) => this.props.onVerifyCode(this.state.code.trim())}
            >
              <FormattedMessage id="Verify Code" />
            </Button>
            <Button
              className="infoScreenButton"
              variant="secondary"
              id="cancelMfaSetup"
              disabled={this.props.dataState === DataState.SAVE_REQ}
              onClick={(_event) => {
                this.setState({ code: "" });
                this.props.onCancelSetup();
              }}
            >
              <FormattedMessage id="Cancel" />
            </Button>
          </div>
        </form>
      </>
    );
  }

  private isCodeValid() {
    return /^\d{6}$/.test(this.state.code.trim());
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserMfaSettingsComponent);
